import { Button } from "@/components/ui/button";
import { Search, ShieldCheck, Zap, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-artisan.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";

const HeroSection = () => {
  const { t } = useLanguage();
  const lp = useLocalizedPath();

  const stats = [
    { icon: ShieldCheck, label: t("trust.verified") },
    { icon: Zap,         label: t("trust.fast") },
    { icon: Tag,         label: t("trust.free") },
  ];

  return (
    <section className="relative overflow-hidden min-h-[88vh] flex items-center">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Artisan algérien au travail"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      </div>

      <div className="container relative z-10 py-20 md:py-28">
        <div className="max-w-2xl space-y-7 animate-fade-up">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            <span>{t("hero.badge")}</span>
          </div>

          {/* Headline */}
          <h1 className="font-display text-4xl font-bold leading-[1.1] rtl:leading-[1.5] text-white drop-shadow-lg md:text-5xl lg:text-6xl">
            {t("hero.title_1")}{" "}
            <span className="inline-block pb-[0.2em] bg-clip-text" style={{ WebkitTextFillColor: "transparent", background: "linear-gradient(135deg, hsl(38 92% 70%) 0%, hsl(45 96% 75%) 100%)", WebkitBackgroundClip: "text" }}>
              {t("hero.title_highlight")}
            </span>
            {" "}{t("hero.title_2")}
          </h1>

          <p className="text-lg text-white/95 md:text-xl leading-relaxed max-w-lg drop-shadow-sm">
            {t("hero.subtitle")}
          </p>

          {/* CTAs */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to={lp("/annonces")}>
              <Button variant="hero" size="xl" className="w-full sm:w-auto gap-2 shadow-warm-lg">
                <Search className="h-5 w-5" />
                {t("hero.cta_annonces")}
                <DirectionalArrow className="h-4 w-4 opacity-70" />
              </Button>
            </Link>
            <Link to={lp("/artisan")}>
              <Button
                size="xl"
                className="w-full sm:w-auto bg-white/15 text-white border border-white/35 hover:bg-white/25 backdrop-blur-sm gap-2 transition-colors"
              >
                {t("hero.cta_artisan")}
              </Button>
            </Link>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2">
            {stats.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-sm text-white/90 drop-shadow-sm">
                <Icon className="h-3.5 w-3.5 text-accent shrink-0" aria-hidden="true" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
